import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import TreatmentPlanItem from 'components/TreatmentPlan/Item';
import ChargeItem from 'components/Billing/Charges/Item';
import RadioFilter from 'components/RadioFilter';
import DropdownFilter from 'components/DropdownFilter';
import { STATUS_FILTERS, TYPE_OPTIONS, getBillingParams } from 'components/Billing/helper';
import { connect } from 'react-redux';
import { BillingActions } from 'store/actions';
import toast from 'react-hot-toast';
import { PortalSpinner } from 'components/PortalSpinner';
import InfiniteScroll from 'react-infinite-scroll-component'

let enhancer = connect(null, { ...BillingActions });

let initialParams = {
  page: 1,
  items: 10
}

const List = ({ getBilling }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingData, setBillingData] = useState(null);
  const [params, setParams] = useState(initialParams);
  const [pageEnd, setPageEnd] = useState(false);
  const [outstandingSummary, setOutstandingSummary] = useState(null);

  const { unpaid_treatment_plan_count, unpaid_charge_count, balance_due } = outstandingSummary || {}

  const statusFilter = searchParams.get('status') || 'all';
  const typesParam = searchParams.get('types');
  const selectedTypes = typesParam ? typesParam.split(',') : ['treatment', 'charge'];

  const setStatusFilter = (value: string) => {
    setSearchParams(prev => { prev.set('status', value); return prev; }, { replace: true });
  };

  const setSelectedTypes = (valueOrUpdater: string[] | ((prev: string[]) => string[])) => {
    const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(selectedTypes) : valueOrUpdater;
    setSearchParams(prev => { prev.set('types', next.join(',')); return prev; }, { replace: true });
  };

  useEffect(() => {
    loadBillings()
  }, [statusFilter, typesParam]);

  const loadBillings = async (_params = initialParams) => {
    try {
      const { data, pagy, meta } = await getBilling({ ..._params, ...getBillingParams(statusFilter, selectedTypes) });

      setBillingData(prev => _params.page == 1 ? data : [...(prev ?? []), ...data])
      setOutstandingSummary(meta)
      setParams(_params)
      setPageEnd(!pagy?.next)

    } catch (error) {
      toast.error(error.errors || 'Failed to load billing data');
      setBillingData([]);
    }
  };

  const loadMore = () => {
    loadBillings({ ...params, page: params.page + 1 });
  }

  if (!billingData) return <PortalSpinner />;

  return (
    <div className="space-y-3">
      <div className="bg-gradient-to-br from-secondary-900 to-slate-700 rounded-2xl p-5 mb-5 text-white">
        <p className="text-xs opacity-60 mb-2 uppercase tracking-wide font-medium">Balance Due</p>
        <p className="text-4xl font-bold tracking-tight">
          ${balance_due.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </p>
        <p className="text-xs opacity-50 mt-2">
          {unpaid_treatment_plan_count} treatment plan{unpaid_treatment_plan_count > 1 ? 's' : ''} · {unpaid_charge_count} unpaid charge{unpaid_charge_count > 1 ? 's' : ''}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <RadioFilter
          options={STATUS_FILTERS}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        <DropdownFilter
          options={TYPE_OPTIONS}
          value={selectedTypes}
          onChange={setSelectedTypes}
        />
      </div>

      {billingData.length === 0 ? (
        <div className="py-16 text-center text-sm text-secondary-400">No activity found.</div>
      ) : (
        <InfiniteScroll
          dataLength={billingData.length}
          next={loadMore}
          hasMore={!pageEnd}
          loader={<div className="col-span-full flex justify-center py-3"><PortalSpinner /></div>}
          className="grid grid-cols-1 gap-4"
        >
          {billingData.map((item) => {
            let { resource_type, resource, amount } = item.attributes || {}

            return resource_type === 'TreatmentPlan'
              ? <TreatmentPlanItem key={item.id} data={resource?.data} amount={amount} />
              : <ChargeItem key={item.id} data={item} />
          })}
        </InfiniteScroll>
      )}
    </div>
  );
};

export default enhancer(List);
